import type { ViewContainerRef } from '@angular/core';
import { inject, Injectable } from '@angular/core';

import type { HelpTranslationLinkMapKey } from '@core/help-articles-legacy';
import { I18nService } from '@core/i18n-legacy';
import type { DialogRef } from '@core/user-interface-legacy';
import { DialogService, DialogWidth } from '@core/user-interface-legacy';

import type { TutorialDialogModuleTranslations } from './translations';
import { TutorialTranslations } from './translations';
import { TutorialDialogComponent } from './tutorial-dialog.component';
import type { TutorialDialogContext, TutorialStep } from './tutorial-dialog.types';
import { ASSETS_PROVIDER } from './tutorial-dialog.types';
import { GlobalProductUpdatesState } from '../../services/global-product-updates.state';

@Injectable({ providedIn: 'root' })
export class TutorialDialogService {
  private readonly dialogService = inject(DialogService);
  private readonly i18n = inject(I18nService);
  public readonly translations = inject<TutorialDialogModuleTranslations>(TutorialTranslations);

  public readonly assetsProvider = inject(ASSETS_PROVIDER);
  public readonly MULTILANG_INTRO_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('multilang-intro/multilang-intro-step-1.png'),
      imgUrl2x: this.assetsProvider('multilang-intro/multilang-intro-step-1_2x.png'),
      title:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_1.title),
      description:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('multilang-intro/multilang-intro-step-2.png'),
      imgUrl2x: this.assetsProvider('multilang-intro/multilang-intro-step-2_2x.png'),
      title:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_2.title),
      description:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('multilang-intro/multilang-intro-step-3.png'),
      imgUrl2x: this.assetsProvider('multilang-intro/multilang-intro-step-3_2x.png'),
      title:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_3.title),
      description:
        this.translations.tutorials.multilang_intro &&
        this.i18n.translate(this.translations.tutorials.multilang_intro.step_3.description),
    },
  ];

  public readonly REFERRALS_ONBOARDING_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-0.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_0.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_0.description),
      isStep0: true,
    },
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-2.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_2.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-3.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_3.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_3.description),
    },
    {
      imgUrl: this.assetsProvider('referrals-onboarding/referrals-onboarding-step-4.svg'),
      title:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_4.title),
      description:
        this.translations.tutorials.referrals_onboarding &&
        this.i18n.translate(this.translations.tutorials.referrals_onboarding.step_4.description),
    },
  ];

  public readonly REQUISITIONS_INTRO_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('requisitions-intro/requisitions-intro-step-1.svg'),
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_1.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-intro/requisitions-intro-step-2.svg'),
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_2.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_2.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-intro/requisitions-intro-step-3.svg'),
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_3.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_3.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-intro/requisitions-intro-step-4.svg'),
      title:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_4.title),
      description:
        this.translations.tutorials.requisitions_intro &&
        this.i18n.translate(this.translations.tutorials.requisitions_intro.step_4.description),
      articleLinkKey:
        this.translations.tutorials.requisitions_intro &&
        <HelpTranslationLinkMapKey>'usingRequisitionApprovalsInTheHiringProcess',
    },
  ];

  public readonly REQUISITIONS_JOB_CONFIG: TutorialStep[] = [
    {
      imgUrl: this.assetsProvider('requisitions-job/requisitions-job-step-1.svg'),
      title:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_1.title),
      description:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_1.description),
    },
    {
      imgUrl: this.assetsProvider('requisitions-job/requisitions-job-step-2.svg'),
      title:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_2.title),
      description:
        this.translations.tutorials.requisitions_job &&
        this.i18n.translate(this.translations.tutorials.requisitions_job.step_2.description),
      articleLinkKey:
        this.translations.tutorials.requisitions_job &&
        <HelpTranslationLinkMapKey>'hiringCandidatesFillingJobOpening',
    },
  ];

  public tutorials: { [key in TutorialDialogContext]: TutorialStep[] } = {
    multilang_intro: this.MULTILANG_INTRO_CONFIG,
    referrals_onboarding: this.REFERRALS_ONBOARDING_CONFIG,
    requisitions_intro: this.REQUISITIONS_INTRO_CONFIG,
    requisitions_job: this.REQUISITIONS_JOB_CONFIG,
  };

  private analyticsContextMap: { [key in TutorialDialogContext]: string } = {
    multilang_intro: '[Settings/Langauges]',
    referrals_onboarding: '[ReferralsOnboarding]',
    requisitions_intro: '[Requisition]',
    requisitions_job: '[Offer]',
  };

  private globalProductUpdatesState = inject(GlobalProductUpdatesState);

  constructor() {
    this.bindDialogGlobalTrigger();
  }

  public open(context: TutorialDialogContext, vcRef: ViewContainerRef): DialogRef | null {
    if (!this.globalProductUpdatesState.enabled) return null;

    return this.dialogService.custom(
      TutorialDialogComponent,
      { width: DialogWidth.Small, showCloseButton: false },
      { config: this.tutorials[context], analyticsContext: this.analyticsContextMap[context] },
      vcRef,
    );
  }

  private bindDialogGlobalTrigger(): void {
    // @ts-ignore
    window['tutorialDialog'] = (context: TutorialDialogContext) => {
      this.open(context, <any>null);
    };
  }
}
